import { useEffect, useMemo, useRef, useState } from "react";
import type { InstaQLEntity } from "@instantdb/react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Globe,
  MessageCircle,
  X,
} from "lucide-react";
import {
  approveAdminSourceRequest,
  fetchAdminSourceEvents,
  rejectAdminSourceRequest,
} from "../adminApi.ts";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import type { EventEntry } from "../../../src/fields.ts";
import { stripTrailingNumeral } from "../../../src/groupNameUtil.ts";
import type schema from "../../../instant.schema.ts";

type SourceRequest = InstaQLEntity<typeof schema, "sourceRequests">;

const statuses = ["pending", "approved", "rejected"] as const;
type Status = typeof statuses[number];

type Toast = {
  id: number;
  kind: "info" | "success" | "error";
  title: string;
  description?: string;
  pending?: boolean;
};

const formatDate = (timestamp?: number) =>
  timestamp
    ? new Date(timestamp).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : "-";

const getBucketLabel = (timestamp?: number) => {
  if (!timestamp) return "Older";
  const now = new Date();
  const date = new Date(timestamp);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const sevenDaysAgoStart = todayStart - 6 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgoStart = todayStart - 29 * 24 * 60 * 60 * 1000;

  const t = date.getTime();
  if (t >= todayStart) {
    return "Today";
  }
  if (t >= yesterdayStart) {
    return "Yesterday";
  }
  if (t >= sevenDaysAgoStart) {
    return "This Week";
  }
  if (t >= thirtyDaysAgoStart) {
    return "This Month";
  }
  return "Older";
};

const groupClustersByDate = (clusters: SourceRequest[][]) =>
  clusters.reduce<{ label: string; clusters: SourceRequest[][] }[]>(
    (acc, cluster) => {
      const primary = cluster[0];
      const label = getBucketLabel(primary?.createdAt);
      const existing = acc.find((b) => b.label === label);
      if (existing) {
        existing.clusters.push(cluster);
      } else {
        acc.push({ label, clusters: [cluster] });
      }
      return acc;
    },
    [],
  );

const statusLabelClass: Record<Status, string> = {
  pending: "text-amber-200 border-amber-500/30 bg-amber-500/10",
  approved: "text-emerald-200 border-emerald-500/30 bg-emerald-500/10",
  rejected: "text-red-200 border-red-500/30 bg-red-500/10",
};

const kindLabel = (kind: string) =>
  kind === "whatsapp-group"
    ? "WhatsApp group"
    : kind === "link-scrape-source"
    ? "Website source"
    : "Instagram page";

const payloadSummary = (request: SourceRequest) =>
  request.kind === "instagram-page"
    ? request.instagramUsername
      ? `@${request.instagramUsername}`
      : request.sourceUrl
    : request.title;

const whatsappDecision = (request: SourceRequest) =>
  request.kind === "whatsapp-group" &&
    (request.payload as { kind?: string; recommendJoin?: boolean }).kind ===
      "whatsapp-group"
    ? (request.payload as { recommendJoin: boolean }).recommendJoin
      ? "Recommended"
      : "Skip"
    : null;

const whatsappChatTitle = (request: SourceRequest) =>
  request.kind === "whatsapp-group" && request.discoverySource === "spy-bot"
    ? (request.payload as { chatTitle?: string }).chatTitle
    : undefined;

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) =>
  value === undefined || value === null || value === ""
    ? null
    : (
      <div className="text-sm text-slate-300">
        <span className="text-slate-400">{label}:</span> {String(value)}
      </div>
    );

const toastClass: Record<Toast["kind"], string> = {
  info: "border-cyan-400/30 bg-cyan-500/10 text-cyan-100",
  success: "border-emerald-400/30 bg-emerald-500/10 text-emerald-100",
  error: "border-red-400/30 bg-red-500/10 text-red-100",
};

const ToastViewport = ({
  toasts,
}: {
  toasts: Toast[];
}) => (
  <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`rounded-xl border px-4 py-3 shadow-xl backdrop-blur-md ${
          toastClass[toast.kind]
        }`}
      >
        <div className="flex items-center gap-2">
          {toast.pending && <Spinner className="h-4 w-4" />}
          <div className="text-sm font-semibold">{toast.title}</div>
        </div>
        {toast.description && (
          <div className="mt-1 text-xs opacity-90">{toast.description}</div>
        )}
      </div>
    ))}
  </div>
);

const formatEventDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const SourceEventRow = ({ event }: { event: EventEntry }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-1">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="font-medium text-white truncate">
          {event.structured.name}
        </div>
        <div className="text-xs text-gray-400">
          {formatEventDate(event.structured.timestamp.start)}
        </div>
        {event.structured.venue && (
          <div className="text-xs text-gray-500">{event.structured.venue}</div>
        )}
      </div>
      {event.structured.links[0] && (
        <a
          href={event.structured.links[0]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-purple-400 hover:text-purple-300 shrink-0"
        >
          Link
        </a>
      )}
    </div>
  </div>
);

type SelectedSource = {
  label: string;
  sourceKind: "instagram-page" | "whatsapp-group" | "link-scrape-source";
  sourceRef: string;
};

const clusterKey = (request: SourceRequest) =>
  request.kind === "whatsapp-group"
    ? `wa:${stripTrailingNumeral(request.title).toLowerCase()}`
    : `ig:${request.instagramUsername ?? request.sourceUrl}`;

const clusterRequests = (requests: SourceRequest[]) =>
  Object.values(
    requests.reduce<Record<string, SourceRequest[]>>((acc, request) => {
      const key = clusterKey(request);
      return { ...acc, [key]: [...(acc[key] ?? []), request] };
    }, {}),
  );

const discoverySourceLabel = (source?: string) =>
  source === "spy-bot"
    ? "Spy bot discovery"
    : source === "user-request"
    ? "User request"
    : null;

const RequestDetails = ({ request }: { request: SourceRequest }) => (
  <>
    <div className="space-y-1">
      <DetailRow label="Created" value={formatDate(request.createdAt)} />
      <DetailRow
        label="Discovery"
        value={discoverySourceLabel(request.discoverySource)}
      />
      <DetailRow
        label="Discovered in"
        value={whatsappChatTitle(request)}
      />
      {request.discoverySource !== "spy-bot" && (
        <DetailRow label="Requester" value={request.requesterUserId} />
      )}
      <DetailRow label="Requester email" value={request.requesterEmail} />
      <DetailRow label="Source URL" value={request.sourceUrl} />
      {request.kind === "link-scrape-source" && (
        <DetailRow label="Source name" value={request.title} />
      )}
      <DetailRow label="Members" value={request.memberCount} />
      <DetailRow label="AI decision" value={whatsappDecision(request)} />
      <DetailRow label="Recommendation" value={request.recommendation} />
      <DetailRow label="Reviewed" value={formatDate(request.reviewedAt)} />
      <DetailRow label="Reviewer" value={request.reviewerEmail} />
    </div>
    <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-200 whitespace-pre-wrap break-words">
      {request.requestText || "No message text provided."}
    </div>
  </>
);

const ActionButtons = (
  { busy, onApprove, onReject }: {
    busy: boolean;
    onApprove: () => void;
    onReject: () => void;
  },
) => (
  <div className="flex gap-3">
    <button
      type="button"
      disabled={busy}
      onClick={onApprove}
      className="flex items-center gap-2 rounded-xl bg-emerald-500/20 px-4 py-2 text-emerald-100 border border-emerald-500/30 disabled:opacity-50"
    >
      <Check size={16} /> Approve
    </button>
    <button
      type="button"
      disabled={busy}
      onClick={onReject}
      className="flex items-center gap-2 rounded-xl bg-red-500/20 px-4 py-2 text-red-100 border border-red-500/30 disabled:opacity-50"
    >
      <X size={16} /> Reject
    </button>
  </div>
);

const ClusterCard = (
  { cluster, busyId, onAction }: {
    cluster: SourceRequest[];
    busyId: string | null;
    onAction: (
      cluster: SourceRequest[],
      primaryId: string,
      action: "approve" | "reject",
    ) => void;
  },
) => {
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const primary = cluster[activeIndex] ?? cluster[0];
  const status = primary.status as Status;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-xl">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/5"
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
              {kindLabel(primary.kind)}
              {cluster.length > 1 && ` · ${cluster.length} variants`}
            </div>
            <div className="font-semibold text-white truncate">
              {payloadSummary(primary)}
              {whatsappChatTitle(primary) && (
                <span className="text-xs text-slate-400 font-normal ml-2">
                  from {whatsappChatTitle(primary)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {discoverySourceLabel(primary.discoverySource) && (
            <span
              className={`text-xs rounded-full border px-2 py-0.5 ${
                primary.discoverySource === "spy-bot"
                  ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-200"
                  : "border-purple-500/30 bg-purple-500/10 text-purple-200"
              }`}
            >
              {discoverySourceLabel(primary.discoverySource)}
            </span>
          )}
          {primary.memberCount != null && (
            <span className="text-xs text-slate-400">
              {primary.memberCount} members
            </span>
          )}
          <span
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              statusLabelClass[status]
            }`}
          >
            {status}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 px-5 py-4 space-y-4">
          {cluster.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {cluster.map((r, i) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setActiveIndex(i)}
                  className={`rounded-lg border px-3 py-1 text-xs ${
                    i === activeIndex
                      ? "border-white/40 bg-white/10 text-white"
                      : "border-white/10 text-slate-300 hover:bg-white/5"
                  }`}
                >
                  {r.title || r.sourceUrl}
                </button>
              ))}
            </div>
          )}
          <RequestDetails request={primary} />
          {status === "pending" && (
            <div className="flex items-center justify-between">
              {whatsappDecision(primary) && (
                <span
                  className={`text-xs rounded-full border px-2 py-0.5 ${
                    whatsappDecision(primary) === "Recommended"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  }`}
                >
                  AI: {whatsappDecision(primary)}
                </span>
              )}
              <ActionButtons
                busy={busyId === primary.id}
                onApprove={() => onAction(cluster, primary.id, "approve")}
                onReject={() => onAction(cluster, primary.id, "reject")}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const useAutoLoadNext = (
  canLoadNextPage: boolean,
  loadNextPage: () => void,
) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canLoadNextPage) return;
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) =>
        entries.some((e) => e.isIntersecting) && loadNextPage(),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadNextPage, loadNextPage]);
  return sentinelRef;
};

const pageSize = 20;

const RequestsSection = (
  { status, busyId, hiddenIds, onAction, recommendationFilter }: {
    status: string;
    busyId: string | null;
    hiddenIds: Set<string>;
    onAction: (
      cluster: SourceRequest[],
      primaryId: string,
      action: "approve" | "reject",
    ) => void;
    recommendationFilter?: "user-requested" | "recommended" | "skipped";
  },
) => {
  const { data, isLoading, error, canLoadNextPage, loadNextPage } = db
    .useInfiniteQuery({
      sourceRequests: {
        $: {
          where: { status },
          order: { createdAt: "desc" },
          limit: pageSize,
        },
      },
    });
  const requests = ((data?.sourceRequests ?? []) as SourceRequest[]).filter(
    (r) => !hiddenIds.has(r.id),
  );
  const clusters = useMemo(
    () => {
      const raw = status === "pending"
        ? clusterRequests(requests)
        : requests.map((r) => [r]);
      if (!recommendationFilter) return raw;
      return raw.filter((c) => {
        const primary = c[0];
        if (recommendationFilter === "user-requested") {
          return primary.discoverySource === "user-request";
        }
        if (primary.discoverySource === "user-request") {
          return false;
        }
        const decision = whatsappDecision(primary as SourceRequest);
        return recommendationFilter === "recommended"
          ? decision === "Recommended"
          : decision !== "Recommended";
      });
    },
    [requests, status, recommendationFilter],
  );
  const sentinelRef = useAutoLoadNext(canLoadNextPage, loadNextPage);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    Today: true,
    Yesterday: true,
  });
  const groupedBuckets = useMemo(
    () => groupClustersByDate(clusters),
    [clusters],
  );
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium capitalize text-white">
          {recommendationFilter
            ? recommendationFilter === "user-requested"
              ? "User Requested"
              : recommendationFilter === "recommended"
              ? "AI Recommended"
              : "AI Skipped"
            : status}
        </h2>
        <div className="text-sm text-gray-500">
          {clusters.length} {status === "pending" ? "groups" : "items"}
          {requests.length !== clusters.length && ` (${requests.length} total)`}
        </div>
      </div>
      {error && <div className="text-red-400">{error.message}</div>}
      {isLoading
        ? <Spinner />
        : requests.length === 0
        ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-gray-500">
            No {status} requests.
          </div>
        )
        : (
          <div className="space-y-6">
            {groupedBuckets.map(({ label, clusters: bucketClusters }) => {
              const isExpanded = expandedSections[label] ??
                (label === "Today" || label === "Yesterday");
              return (
                <div key={label} className="space-y-3">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedSections((prev) => ({
                        ...prev,
                        [label]: !isExpanded,
                      }))}
                    className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition w-full text-left py-1 border-b border-white/5"
                  >
                    {isExpanded
                      ? <ChevronDown size={14} />
                      : <ChevronRight size={14} />}
                    <span>{label}</span>
                    <span className="text-xs text-slate-500 bg-white/5 px-1.5 py-0.5 rounded-full ml-1">
                      {bucketClusters.length}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="space-y-3">
                      {bucketClusters.map((cluster) => (
                        <ClusterCard
                          key={cluster[0].id}
                          cluster={cluster}
                          busyId={busyId}
                          onAction={onAction}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={sentinelRef} className="h-8" />
            {canLoadNextPage && (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            )}
          </div>
        )}
    </section>
  );
};

export const AdminRequests = () => {
  const user = db.useAuth().user;
  const [activeStatus, setActiveStatus] = useState<Status>("pending");
  const [pendingTab, setPendingTab] = useState<
    "user-requested" | "recommended" | "skipped"
  >("user-requested");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  const pushToast = (toast: Omit<Toast, "id">) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((current) => [...current, { ...toast, id }]);
    if (!toast.pending) {
      setTimeout(
        () => setToasts((current) => current.filter((item) => item.id !== id)),
        3500,
      );
    }
    return id;
  };

  const replaceToast = (id: number, toast: Omit<Toast, "id">) => {
    setToasts((current) =>
      current.map((item) => item.id === id ? { ...toast, id } : item)
    );
    setTimeout(
      () => setToasts((current) => current.filter((item) => item.id !== id)),
      3500,
    );
  };

  const act = async (
    cluster: SourceRequest[],
    requestId: string,
    action: "approve" | "reject",
  ) => {
    const auth = await db.getAuth();
    const token = auth?.refresh_token;
    if (!token) {
      setMessage("Missing auth token");
      return;
    }
    const clusterIds = cluster.map((r) => r.id);
    setHiddenIds((prev) => new Set([...prev, ...clusterIds]));
    setBusyId(requestId);
    setMessage("");
    const toastId = pushToast({
      kind: "info",
      pending: true,
      title: action === "approve" ? "Approving request" : "Rejecting request",
    });
    const promise = action === "approve"
      ? approveAdminSourceRequest(token, requestId)
      : rejectAdminSourceRequest(token, requestId);
    promise
      .then(() =>
        replaceToast(toastId, {
          kind: "success",
          title: action === "approve" ? "Request approved" : "Request rejected",
        })
      )
      .catch((err: unknown) => {
        setHiddenIds((prev) => {
          const next = new Set(prev);
          clusterIds.forEach((id) => next.delete(id));
          return next;
        });
        const errorMessage = err instanceof Error
          ? err.message
          : `Failed to ${action} request`;
        setMessage(errorMessage);
        replaceToast(toastId, {
          kind: "error",
          title: action === "approve"
            ? "Failed to approve request"
            : "Failed to reject request",
          description: errorMessage,
        });
      })
      .finally(() => setBusyId(null));
  };

  if (!user) return <Spinner />;

  return (
    <div className="space-y-6">
      <ToastViewport toasts={toasts} />
      {message && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100">
          {message}
        </div>
      )}
      <div className="flex gap-2">
        {statuses.map((status) => (
          <button
            type="button"
            key={status}
            onClick={() => setActiveStatus(status)}
            className={`rounded-xl border px-4 py-2 text-sm capitalize ${
              activeStatus === status
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            {status}
          </button>
        ))}
      </div>
      {activeStatus === "pending" && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPendingTab("user-requested")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "user-requested"
                ? "border-purple-500/30 bg-purple-500/10 text-purple-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            User Requested
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("recommended")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "recommended"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            AI Recommended
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("skipped")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "skipped"
                ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            AI Skipped
          </button>
        </div>
      )}
      <RequestsSection
        key={`${activeStatus}-${pendingTab}`}
        status={activeStatus}
        busyId={busyId}
        hiddenIds={hiddenIds}
        onAction={act}
        recommendationFilter={activeStatus === "pending"
          ? pendingTab
          : undefined}
      />
    </div>
  );
};

const useSourceEvents = (selectedSource: SelectedSource | null) => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedSource) return;
    const mounted = { current: true };
    setLoading(true);
    setError(null);
    db.getAuth()
      .then((auth) => {
        if (!auth?.refresh_token) throw new Error("Missing auth token");
        return fetchAdminSourceEvents(
          auth.refresh_token,
          selectedSource.sourceKind,
          selectedSource.sourceRef,
        );
      })
      .then((result) => {
        if (mounted.current) setEvents(result);
      })
      .catch((err) => {
        if (mounted.current) {
          setError(
            err instanceof Error ? err.message : "Failed loading events",
          );
        }
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, [selectedSource?.sourceKind, selectedSource?.sourceRef]);

  return { events, loading, error };
};

const ExpandableSourceCard = (
  { selectedSource, header }: {
    selectedSource: SelectedSource;
    header: React.ReactNode;
  },
) => {
  const [expanded, setExpanded] = useState(false);
  const { events, loading, error } = useSourceEvents(
    expanded ? selectedSource : null,
  );
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/10 transition"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <div className="min-w-0 flex-1">{header}</div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 px-4 py-3 space-y-2 bg-slate-950/40">
          {loading
            ? <Spinner className="h-4 w-4" />
            : error
            ? <div className="text-red-400 text-sm">{error}</div>
            : events.length === 0
            ? (
              <div className="text-gray-500 text-sm">
                No ingested events yet.
              </div>
            )
            : (
              <>
                <div className="text-xs text-gray-500">
                  {events.length} events
                </div>
                {events.map((event) => (
                  <SourceEventRow key={event.id} event={event} />
                ))}
              </>
            )}
        </div>
      )}
    </div>
  );
};

const CoverageColumn = (
  { title, icon, count, children }: {
    title: string;
    icon: React.ReactNode;
    count: number;
    children: React.ReactNode;
  },
) => (
  <section className="space-y-3 min-w-0">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl font-medium text-white">{title}</h2>
      </div>
      <div className="text-sm text-gray-500">{count}</div>
    </div>
    {children}
  </section>
);

export const AdminCoverage = () => {
  const { data, isLoading, error } = db.useQuery({
    instagramSources: { $: { where: { status: "active" } } },
    whatsappGroups: {},
    linkScrapeSources: { $: { where: { status: "active" } } },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-red-400">
        Error loading sources: {error.message}
      </div>
    );
  }

  const instagram = data?.instagramSources ?? [];
  const whatsapp = data?.whatsappGroups ?? [];
  const linkSources = data?.linkScrapeSources ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <CoverageColumn
        title="Instagram Pages"
        icon={<Globe size={20} className="text-purple-400" />}
        count={instagram.length}
      >
        {instagram.length === 0
          ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
              No active Instagram sources.
            </div>
          )
          : (
            <div className="space-y-2">
              {instagram.map((s) => (
                <ExpandableSourceCard
                  key={s.id}
                  selectedSource={{
                    label: `@${s.username}`,
                    sourceKind: "instagram-page",
                    sourceRef: s.url ??
                      `https://www.instagram.com/${s.username}`,
                  }}
                  header={
                    <>
                      <div className="font-medium text-white truncate">
                        @{s.username}
                      </div>
                      {s.categories && s.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {s.categories.map((c) => (
                            <span
                              key={c}
                              className="rounded-full bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 text-xs text-purple-200"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        added by {s.addedBy}
                      </div>
                    </>
                  }
                />
              ))}
            </div>
          )}
      </CoverageColumn>

      <CoverageColumn
        title="WhatsApp Groups"
        icon={<MessageCircle size={20} className="text-emerald-400" />}
        count={whatsapp.length}
      >
        {whatsapp.length === 0
          ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
              No WhatsApp groups.
            </div>
          )
          : (
            <div className="space-y-2">
              {whatsapp.map((g) =>
                g.whatsappId
                  ? (
                    <ExpandableSourceCard
                      key={g.id}
                      selectedSource={{
                        label: g.name ?? g.whatsappId,
                        sourceKind: "whatsapp-group",
                        sourceRef: g.whatsappId,
                      }}
                      header={
                        <>
                          <div className="font-medium text-white truncate">
                            {g.name ?? g.whatsappId}
                          </div>
                          {g.memberCount != null && (
                            <div className="text-xs text-gray-400 mt-1">
                              {g.memberCount} members
                            </div>
                          )}
                          {g.description && (
                            <div className="text-xs text-gray-500 line-clamp-2 mt-1">
                              {g.description}
                            </div>
                          )}
                        </>
                      }
                    />
                  )
                  : null
              )}
            </div>
          )}
      </CoverageColumn>

      <CoverageColumn
        title="Link Sources"
        icon={<Globe size={20} className="text-cyan-400" />}
        count={linkSources.length}
      >
        {linkSources.length === 0
          ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
              No active link sources.
            </div>
          )
          : (
            <div className="space-y-2">
              {linkSources.map((s) => (
                <ExpandableSourceCard
                  key={s.id}
                  selectedSource={{
                    label: s.source,
                    sourceKind: "link-scrape-source",
                    sourceRef: s.source,
                  }}
                  header={
                    <>
                      <div className="font-medium text-white truncate">
                        {s.source}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-1">
                        {s.url}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.useProxy && (
                          <span className="rounded-full bg-cyan-500/20 border border-cyan-500/30 px-2 py-0.5 text-xs text-cyan-200">
                            proxy
                          </span>
                        )}
                        {s.categories?.map((c) => (
                          <span
                            key={c}
                            className="rounded-full bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 text-xs text-purple-200"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </>
                  }
                />
              ))}
            </div>
          )}
      </CoverageColumn>
    </div>
  );
};
